// client/src/utils/ReliableCallManager.js
// Ported from 3cx_testing old/frontend/src/utils/callManager.js
// Manages call state using the reliable 3CX event logic

// Legacy key. Call state is NO LONGER persisted to localStorage — a stale
// snapshot rehydrated on refresh resurrected calls that had already ended,
// showing false Ringing/Connected cards that nothing could heal (the ending
// 3CX events had long since been delivered and would never be re-sent). Call
// state now lives in memory only; the authoritative cross-refresh source is
// the backend's Redis snapshot, pulled by useReliableCalls.hydrateFromServer.
// The key is only referenced to purge leftovers from prior versions.
const LEGACY_STORAGE_KEY = 'bzct_reliable_calls_v1';

class ReliableCallManager {
    constructor() {
        this.calls = new Map();
        this.userExtension = null;
        this._purgeLegacyStorage();
    }

    /** Drop any call state written by an older build of this class. */
    _purgeLegacyStorage() {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return;
            window.localStorage.removeItem(LEGACY_STORAGE_KEY);
        } catch {
            /* noop */
        }
    }

    /**
     * Reconcile a server-side snapshot 
     *
     
     *
     * @param {Object} snapshot - { calls: [[callid, { ...call, participants: [...] }], ...] }
     */
    mergeSnapshot(snapshot) {
        if (!snapshot || !Array.isArray(snapshot.calls)) return;
        for (const [callid, call] of snapshot.calls) {
            const participants = new Map(
                Array.isArray(call.participants) ? call.participants : []
            );
            this.calls.set(String(callid), { ...call, participants });
        }
    }

    /**
     * Set current user extension to filter calls
     * @param {String|Number} extension 
     */
    setUserExtension(extension) {
        this.userExtension = extension ? String(extension) : null;
        // console.log(`📞 ReliableCallManager: Filter set to extension ${this.userExtension}`);
    }

    /**
     * Adds or updates a participant in the call map
     * @param {Object} callData - The reliable_participant_data message data
     */
    addOrUpdateParticipant(callData) {
        const {
            sequence,
            event_type,
            participant_data
        } = callData;

        if (!participant_data) return { action: 'ignored' };

        const {
            callid,
            status,
            party_caller_id,
            party_did,
            party_caller_name,
            party_dn,
            id: participant_id,
            dn
        } = participant_data;

        const callidStr = String(callid);
        // Unique key for participant within a call
        const participantKey = `${party_caller_id || 'unknown'}:${party_did || 'unknown'}`;

        // Determine actual status based on event_type 1 (Ended)
        let actualStatus = status;
        if (event_type === 1) {
            actualStatus = 'Ended';
        }

        // Initialize call entry if new
        if (!this.calls.has(callidStr)) {
            this.calls.set(callidStr, {
                callid: callidStr,
                participants: new Map(),
                firstSeen: new Date().toISOString(),
                everConnected: false,
                connectedAt: null,
                endedAt: null
            });
            // console.log(`✨ Reliable: Created new call: ${callidStr}`);
        }

        const call = this.calls.get(callidStr);
        const existingParticipant = call.participants.get(participantKey);

        // Preserve DN from earlier status if current one is missing
        let preservedDn = dn;
        if (!preservedDn && existingParticipant?.dn) {
            preservedDn = existingParticipant.dn;
        }

        const participantData = {
            participantKey,
            sequence,
            status: actualStatus,
            event_type,
            party_caller_id: party_caller_id || existingParticipant?.party_caller_id,
            party_did: party_did || existingParticipant?.party_did,
            party_caller_name: party_caller_name || existingParticipant?.party_caller_name,
            party_dn: party_dn || existingParticipant?.party_dn,
            participant_id: participant_id || existingParticipant?.participant_id,
            dn: preservedDn,
            lastUpdate: new Date().toISOString(),
            updates: existingParticipant ? existingParticipant.updates + 1 : 1
        };

        // Update everConnected and connectedAt if this participant shows connection
        if (actualStatus === 'Connected' || actualStatus === 'Talking') {
            if (!call.everConnected) {
                call.connectedAt = Date.now();
                call.everConnected = true;
            }
        }

        // Track when the call ended. 3CX can emit an early/transient Ended for a
        // non-user leg while the user's own leg is still Connected, then a final
        // Ended when the user actually hangs up. Latching the FIRST Ended froze
        // endedAt ~1s after connectedAt, collapsing the ended-card duration to
        // ~0:01 even though the live timer (which tracks the user's leg) showed
        // the real duration. Advance endedAt to the most recent Ended so the
        // static duration matches what the live timer showed.
        if (actualStatus === 'Ended') {
            call.endedAt = Date.now();
        }

        call.participants.set(participantKey, participantData);
        call.lastUpdate = new Date().toISOString();

        return { action: existingParticipant ? 'updated' : 'added', call };
    }

    /**
     * Handle the reliable_call_ended event
     * @param {Object} data - The reliable_call_ended message data
     */
    handleCallEnded(data) {
        const { entity, sequence } = data;

        // Extract info from entity: /callcontrol/{ext}/participants/{id}
        const match = entity?.match(/\/callcontrol\/(\d+)\/participants\/(\d+)/);
        if (!match) return null;

        const userExt = match[1];
        const participantId = match[2];

        // console.log(`🛑 Reliable End: Extracted ext ${userExt}, participant ${participantId}`);

        // If this end event isn't for our extension, skip
        if (this.userExtension && userExt !== this.userExtension) return null;

        let foundCallId = null;
        for (const [callid, call] of this.calls) {
            for (const participant of call.participants.values()) {
                if (String(participant.participant_id) === String(participantId)) {
                    foundCallId = callid;

                    // Force update status to Ended
                    participant.status = 'Ended';
                    participant.event_type = 1;
                    participant.lastUpdate = new Date().toISOString();
                    call.lastUpdate = new Date().toISOString();
                    // Authoritative end for the user's own leg — always advance
                    // endedAt so a stale early value from a transient leg can't
                    // survive and shrink the displayed duration.
                    call.endedAt = Date.now();
                    // console.log(`✓ Reliable End: Marked call ${callid} as Ended`);
                    break;
                }
            }
            if (foundCallId) break;
        }
        return foundCallId;
    }

    /**
     * Handle a reliable_ringgroup_update — the backend's authoritative status for
     * this member's card in a ring-group call. Drives every member off the same
     * source of truth so the cards stay in lockstep (no per-leg flicker).
     * @param {Object} data - the reliable_ringgroup_update payload
     */
    handleRingGroupUpdate(data) {
        if (!data) return null;
        const { callId, forExt, memberStatus, answeredBy, answeredByName, isAnswerer, phoneNumber, answeredAt, endedAt } = data;
        const callidStr = String(callId);

        let call = this.calls.get(callidStr);
        if (!call) {
            // Never build a card from a TERMINAL update. No local record means we
            // never saw this call start — it began before this tab logged in — so
            // an Ended update has nothing to heal, and a card built from it alone
            // carries everConnected=false with endedAt set, which renders as
            // "Abandoned" for a call that was actually answered. Mirrors the same
            // guard in the backend's reliableCallStore.applyRingGroupUpdate.
            if (memberStatus === 'Ended') return null;
            call = {
                callid: callidStr,
                participants: new Map(),
                firstSeen: new Date().toISOString(),
                everConnected: false,
                connectedAt: null,
                endedAt: null
            };
            this.calls.set(callidStr, call);
        }

        // Seed this member's participant row if its own Ringing leg never landed
        // (getActiveCalls needs a dn === userExtension row to render the card).
        const myExt = String(forExt || this.userExtension || '');
        if (myExt && ![...call.participants.values()].some(p => String(p.dn) === myExt)) {
            const seededKey = `${phoneNumber || 'unknown'}:seeded`;
            call.participants.set(seededKey, {
                participantKey: seededKey,
                dn: myExt,
                party_caller_id: phoneNumber || null,
                party_did: null,
                party_caller_name: phoneNumber || null,
                status: memberStatus === 'Ended' ? 'Ended' : 'Connected',
                event_type: memberStatus === 'Ended' ? 1 : 0,
                seeded: true,
                lastUpdate: new Date().toISOString(),
                updates: 1
            });
        }

        call.isRingGroup = true;
        // Only non-answerers carry an "Answered by <ext>" label.
        call.ringGroupAnsweredBy = isAnswerer ? null : (answeredBy || null);
        // Resolved display name of the answerer (extension shown on hover). Null
        // falls back to the extension on the card.
        call.ringGroupAnsweredByName = isAnswerer ? null : (answeredByName || null);
        if (phoneNumber && !call.callerId) call.callerId = phoneNumber;

        if (memberStatus === 'Ended') {
            call.endedAt = endedAt || Date.now();
            // A ring-group call WITH an answerer was answered by definition, so it
            // must never render as "Abandoned" (callEnded && !timerStarted).
            // Back-fills the connected markers if the Connected update was lost or
            // arrived out of order. A genuine abandon carries answeredBy === null
            // and is unaffected.
            if (answeredBy != null && !call.everConnected) {
                call.everConnected = true;
                if (!call.connectedAt) call.connectedAt = answeredAt || call.endedAt;
            }
        } else {
            call.everConnected = true;
            if (!call.connectedAt) call.connectedAt = answeredAt || Date.now();
            call.endedAt = null;
        }
        call.lastUpdate = new Date().toISOString();

        return callidStr;
    }

    /**
     * Returns a list of active calls relevant to the current user
     */
    getActiveCalls() {
        const result = [];
        if (!this.userExtension) return result;

        for (const [callid, call] of this.calls) {
            const allParticipants = Array.from(call.participants.values());

            // Find the participant entry corresponding to our extension
            const userParticipant = allParticipants.find(p => String(p.dn) === this.userExtension);

            if (!userParticipant) continue;

            // Find the caller participant (different DN and has caller ID)
            const callerParticipant = allParticipants.find(p =>
                String(p.dn) !== this.userExtension && p.party_caller_id
            ) || userParticipant;

            // Ring-group call: render from the authoritative ring-group state the
            // backend pushed (handleRingGroupUpdate), so all members agree and no
            // per-leg removal can flicker the card.
            if (call.isRingGroup) {
                const rgStatus = call.endedAt ? 'Ended' : (call.everConnected ? 'Connected' : 'Ringing');
                // Same 1-hour window the sweeper uses — don't surface stale ended.
                if (rgStatus === 'Ended' && call.endedAt && Date.now() - call.endedAt > 60 * 60 * 1000) {
                    continue;
                }
                const rgFrom = userParticipant.party_caller_id || callerParticipant.party_caller_id || call.callerId;
                let rgDuration = null;
                if (call.connectedAt && call.endedAt) {
                    rgDuration = Math.round((call.endedAt - call.connectedAt) / 1000);
                }
                result.push({
                    callId: callid,
                    phoneNumber: rgFrom,
                    status: rgStatus,
                    callerName: call.resolvedCallerName || userParticipant.party_caller_name || callerParticipant.party_caller_name || rgFrom,
                    resolvedCallerName: call.resolvedCallerName || null,
                    firstSeen: call.firstSeen,
                    startTime: call.connectedAt,
                    callDuration: rgDuration,
                    lastUpdate: call.lastUpdate,
                    participants: allParticipants,
                    isReliable: true,
                    timerStarted: call.everConnected,
                    callEnded: rgStatus === 'Ended',
                    endedAt: call.endedAt,
                    isUnanswered: rgStatus === 'Ended' && !call.everConnected,
                    answeredElsewhereBy: call.ringGroupAnsweredBy || null,
                    answeredElsewhereByName: call.ringGroupAnsweredByName || null,
                    // Backend-persisted customer payload (match/records) so the
                    // card restores from the snapshot when localStorage is empty.
                    customerData: call.customerData || null
                });
                continue;
            }

            // Determine the from number
            const fromNumber = userParticipant.party_caller_id || callerParticipant.party_caller_id;
            const callStatus = userParticipant.status;

            // Filter for standard UI statuses
            if (['Ringing', 'Connected', 'Ended', 'Talking'].includes(callStatus)) {
                // Map 'Talking' back to 'Connected' for UI consistency if needed
                const mappedStatus = callStatus === 'Talking' ? 'Connected' : callStatus;

                // Calculate duration if ended
                let callDuration = null;
                if (call.connectedAt && call.endedAt) {
                    callDuration = Math.round((call.endedAt - call.connectedAt) / 1000);
                }

                result.push({
                    callId: callid,
                    phoneNumber: fromNumber,
                    status: mappedStatus,
                    // Prefer a CRM/Twilio-resolved name persisted on the record
                    // (set by the backend) so a fresh tab that only has the Redis
                    // snapshot still shows the name instead of the phone number.
                    callerName: call.resolvedCallerName || userParticipant.party_caller_name || callerParticipant.party_caller_name || fromNumber,
                    resolvedCallerName: call.resolvedCallerName || null,
                    firstSeen: call.firstSeen,
                    startTime: call.connectedAt, // Timestamp for live timer
                    callDuration: callDuration, // Actual duration in seconds for ended calls
                    lastUpdate: call.lastUpdate,
                    participants: allParticipants,
                    isReliable: true, // Flag to distinguish
                    timerStarted: call.everConnected,
                    callEnded: mappedStatus === 'Ended',
                    endedAt: call.endedAt, // Expose for cleanup logic
                    isUnanswered: mappedStatus === 'Ended' && !call.everConnected,
                    // Backend-persisted customer payload (match/records) so the
                    // card restores from the snapshot when localStorage is empty.
                    customerData: call.customerData || null
                });
            }
        }
        return result;
    }

    /**
     * Remove a call from the manager
     */
    removeCall(callid) {
        this.calls.delete(String(callid));
    }

    /**
     * Clear calls. If `keepCallIds` is provided, calls whose id is in the set
     * are preserved (e.g. a call that is still in progress when the user hits
     * "Clear"); everything else is removed. With no argument, clears all.
     * @param {Set<String>} [keepCallIds]
     */
    clearAll(keepCallIds = null) {
        if (keepCallIds && keepCallIds.size > 0) {
            for (const callid of Array.from(this.calls.keys())) {
                if (!keepCallIds.has(callid)) {
                    this.calls.delete(callid);
                }
            }
            return;
        }

        this.calls.clear();
    }
}

export const reliableCallManager = new ReliableCallManager();
